import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Check, Loader2, Upload } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ReactLenis } from "lenis/react";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { FloatingWhatsApp } from "../components/site/FloatingWhatsApp";
import { IMG } from "../data/assets";
import { fadeUp } from "../lib/motion";
import { useLanguage } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkWithUs() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", additional_info: "" });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useLanguage();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.title = "Work With Us | Mediterranean Palate";
    window.scrollTo(0, 0);
    return () => { document.title = "Private Chef in Rutland & Stamford | Mediterranean Palate"; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error(t("toast.needName")); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("additional_info", form.additional_info);
      if (cv) fd.append("cv", cv);
      await axios.post(`${API}/careers`, fd);
      setDone(true);
      toast.success(t("toast.okWork"));
    } catch (err) { console.error(err); toast.error(t("toast.err")); }
    finally { setLoading(false); }
  };

  return (
    <ReactLenis root options={{ lerp: 0.08, smoothWheel: true }}>
      <div className="grain-overlay" />
      <Toaster theme="dark" position="bottom-right" />
      <Nav />
      <main>
        <section className="relative min-h-[60svh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={IMG.corporate} alt="Join the Mediterranean Palate team" className="photo-grade w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#14180E] via-[#14180E]/80 to-[#14180E]/40" />
          </div>
          <div className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10 pt-28 pb-14">
            <p className="font-mono uppercase tracking-[0.3em] text-[11px] text-[--gold] mb-6">{t("work.eyebrow")}</p>
            <h1 className="font-display font-light tracking-tight leading-[1] text-4xl sm:text-5xl md:text-6xl text-[--text] max-w-3xl">{t("work.h1a")} <span className="italic text-[--gold]">{t("work.h1b")}</span></h1>
            <p className="mt-6 text-[--text-2] text-base md:text-lg max-w-xl">{t("work.intro")}</p>
          </div>
        </section>

        <section className="tiled relative py-12 md:py-16 bg-[--bg]">
          <div className="max-w-[720px] mx-auto px-5 md:px-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div key="s" data-testid="careers-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-2xl p-12 bg-[--surface] flex flex-col items-start gap-6">
                  <div className="w-14 h-14 rounded-full bg-[--gold] flex items-center justify-center"><Check className="text-[#14180E]" strokeWidth={1.5} /></div>
                  <h3 className="font-display text-3xl text-[--text]">{t("work.successH")}</h3>
                  <p className="text-[--text-2] max-w-md">{t("quote.thanks")} {form.name.split(" ")[0]}. {t("work.successP")}</p>
                </motion.div>
              ) : (
                <motion.form key="f" data-testid="careers-form" onSubmit={submit} exit={{ opacity: 0 }} variants={fadeUp} initial="hidden" animate="visible" className="bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-10 grid grid-cols-1 gap-7">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("work.l.name")}</label>
                    <input data-testid="cv-name" className="editorial-input mt-2" value={form.name} onChange={set("name")} placeholder={t("work.p.name")} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("work.l.email")}</label>
                    <input data-testid="cv-email" type="email" className="editorial-input mt-2" value={form.email} onChange={set("email")} placeholder={t("work.p.email")} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("work.l.phone")}</label>
                    <input data-testid="cv-phone" className="editorial-input mt-2" value={form.phone} onChange={set("phone")} placeholder={t("work.p.phone")} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("work.l.info")}</label>
                    <textarea data-testid="cv-additional-info" rows={4} className="editorial-input mt-2 resize-none" value={form.additional_info} onChange={set("additional_info")} placeholder={t("work.p.info")} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("work.l.cv")}</label>
                    <label className="mt-3 flex items-center gap-3 px-5 py-4 rounded-xl border border-dashed border-white/25 cursor-pointer hover:border-[--gold] transition-colors">
                      <Upload size={18} className="text-[--gold]" />
                      <span className="text-[--text-2] text-sm truncate">{cv ? cv.name : t("work.p.cv")}</span>
                      <input data-testid="cv-file" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  <div>
                    <button data-testid="careers-submit" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400 disabled:opacity-60">
                      {loading ? <Loader2 className="animate-spin" size={16} /> : null}{loading ? t("quote.sending") : t("work.submit")}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </ReactLenis>
  );
}
